import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
  StatusBadge,
  THead,
  TableShell,
  Td,
  Th,
  Tr,
  useToast,
} from '@ambernord/ui';
import { BellOff, ClipboardList } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayout, DataState } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDateTime } from '../lib/format.js';

interface TaskItem {
  id: string;
  type: string;
  title: string;
  status: string;
  dueAt: string | null;
  channel: string;
  createdAt: string;
}

interface ReminderItem {
  id: string;
  kind: string;
  fireAt: string;
  firedAt: string | null;
}

const DONE_STATUSES = new Set(['done', 'completed', 'cancelled', 'closed']);

/** Zināmās uzdevumu tipu vērtības (saskaņā ar backend services, kas tos rada). */
const TASK_TYPES = [
  'general',
  'confirm_supply_order',
  'inspect_invoice',
  'carrier_capacity_prepare',
  'open_book_review',
] as const;

function isOpen(status: string): boolean {
  return !DONE_STATUSES.has(status.toLowerCase());
}

function isOverdue(task: TaskItem): boolean {
  if (!task.dueAt || !isOpen(task.status)) return false;
  const due = new Date(task.dueAt);
  return !Number.isNaN(due.getTime()) && due.getTime() < Date.now();
}

export default function Tasks() {
  const { t, i18n } = useTranslation('tasks');
  const { toast } = useToast();

  const [tasks, setTasks] = useState<TaskItem[] | null>(null);
  const [tasksError, setTasksError] = useState(false);

  const [reminders, setReminders] = useState<ReminderItem[] | null>(null);
  const [remindersError, setRemindersError] = useState(false);

  const [title, setTitle] = useState('');
  const [type, setType] = useState<string>(TASK_TYPES[0]);
  const [dueAt, setDueAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [completingId, setCompletingId] = useState<string | null>(null);
  const [completeError, setCompleteError] = useState<string | null>(null);

  const statusLabel = (code: string) => t(`statuses:${code.toLowerCase()}`, { defaultValue: code });
  const typeLabel = (code: string) => t(`createTask.types.${code}`, { defaultValue: code });

  const loadTasks = useCallback(async () => {
    setTasksError(false);
    const res = await api.get<{ items: TaskItem[] }>('/api/tasks');
    if (!res.ok) {
      setTasksError(true);
      setTasks([]);
      return;
    }
    setTasks(res.data.items ?? []);
  }, []);

  const loadReminders = useCallback(async () => {
    setRemindersError(false);
    const res = await api.get<{ items: ReminderItem[] }>('/api/tasks/reminders');
    if (!res.ok) {
      setRemindersError(true);
      setReminders([]);
      return;
    }
    setReminders(res.data.items ?? []);
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: TaskItem[] }>('/api/tasks');
      if (!active) return;
      if (!res.ok) {
        setTasksError(true);
        setTasks([]);
        return;
      }
      setTasks(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: ReminderItem[] }>('/api/tasks/reminders');
      if (!active) return;
      if (!res.ok) {
        setRemindersError(true);
        setReminders([]);
        return;
      }
      setReminders(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, []);

  async function onComplete(id: string) {
    setCompleteError(null);
    setCompletingId(id);
    try {
      const res = await api.post<{ error?: string }>(`/api/tasks/${id}/complete`);
      if (!res.ok) {
        const code = res.data.error ?? '';
        const msg =
          res.status === 403
            ? t('errors.notPermitted')
            : t('errors.completeGeneric', { code: code || res.status });
        setCompleteError(msg);
        toast(msg, 'error');
        return;
      }
      toast(t('toast.completed'));
      await loadTasks();
    } catch {
      const msg = t('errors.completeFailed');
      setCompleteError(msg);
      toast(msg, 'error');
    } finally {
      setCompletingId(null);
    }
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!title.trim()) {
      setFormError(t('errors.titleRequired'));
      return;
    }
    if (!type.trim()) {
      setFormError(t('errors.typeRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post<{ error?: string }>('/api/tasks', {
        type: type.trim(),
        title: title.trim(),
        ...(dueAt ? { dueAt: new Date(dueAt).toISOString() } : {}),
      });
      if (!res.ok) {
        const code = res.data.error ?? '';
        const msg =
          res.status === 403
            ? t('errors.notPermitted')
            : t('errors.createGeneric', { code: code || res.status });
        setFormError(msg);
        toast(msg, 'error');
        return;
      }
      setTitle('');
      setType(TASK_TYPES[0]);
      setDueAt('');
      toast(t('toast.created'));
      await Promise.all([loadTasks(), loadReminders()]);
    } catch {
      const msg = t('errors.createFailed');
      setFormError(msg);
      toast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppLayout title={t('title')}>
      <div className="flex flex-col gap-8">
        {/* Izveidot uzdevumu */}
        <Card>
          <CardHeader title={t('createTask.heading')} />
          <CardBody>
            <form
              className="flex flex-col gap-4 sm:flex-row sm:items-end"
              onSubmit={(e) => void onCreate(e)}
              noValidate
            >
              <Field label={t('createTask.title')} htmlFor="task-title" className="flex-1">
                <Input
                  id="task-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>
              <Field label={t('createTask.type')} htmlFor="task-type">
                <Select id="task-type" value={type} onChange={(e) => setType(e.target.value)}>
                  {TASK_TYPES.map((v) => (
                    <option key={v} value={v}>
                      {typeLabel(v)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('createTask.due')} htmlFor="task-due">
                <Input
                  id="task-due"
                  type="date"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                />
              </Field>
              <Button type="submit" disabled={submitting}>
                {submitting ? t('createTask.submitting') : t('createTask.submit')}
              </Button>
            </form>
            {formError && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
                {formError}
              </p>
            )}
          </CardBody>
        </Card>

        {/* Uzdevumi */}
        <section className="flex flex-col gap-4">
          <h2 className="text-sm font-medium">{t('tasks.heading')}</h2>
          {completeError && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {completeError}
            </p>
          )}
          {tasksError ? (
            <DataState loading={false} error={t('tasks.loadError')} empty={false}>
              {null}
            </DataState>
          ) : tasks === null ? (
            <SkeletonRows rows={4} />
          ) : tasks.length === 0 ? (
            <EmptyState
              icon={<ClipboardList className="h-6 w-6" />}
              title={t('tasks.empty.title')}
              hint={t('tasks.empty.hint')}
            />
          ) : (
            <TableShell>
              <THead>
                <tr>
                  <Th>{t('tasks.colTitle')}</Th>
                  <Th>{t('tasks.colType')}</Th>
                  <Th>{t('tasks.colStatus')}</Th>
                  <Th>{t('tasks.colDue')}</Th>
                  <Th />
                </tr>
              </THead>
              <tbody>
                {tasks.map((task) => (
                  <Tr key={task.id}>
                    <Td className="font-medium text-slate-900 dark:text-white">{task.title}</Td>
                    <Td>{typeLabel(task.type)}</Td>
                    <Td>
                      <StatusBadge
                        status={isOverdue(task) ? 'overdue' : task.status}
                        label={statusLabel(task.status)}
                      />
                    </Td>
                    <Td
                      className={
                        isOverdue(task) ? 'font-medium text-red-600 dark:text-red-400' : undefined
                      }
                    >
                      {formatDateTime(task.dueAt, i18n.language)}
                    </Td>
                    <Td className="text-right">
                      {isOpen(task.status) && (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => void onComplete(task.id)}
                          disabled={completingId === task.id}
                        >
                          {completingId === task.id ? t('tasks.completing') : t('tasks.complete')}
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </TableShell>
          )}
        </section>

        {/* Atgādinājumi */}
        <section className="flex flex-col gap-4">
          <h2 className="text-sm font-medium">{t('reminders.heading')}</h2>
          {remindersError ? (
            <DataState loading={false} error={t('reminders.loadError')} empty={false}>
              {null}
            </DataState>
          ) : reminders === null ? (
            <SkeletonRows rows={3} />
          ) : reminders.length === 0 ? (
            <EmptyState
              icon={<BellOff className="h-6 w-6" />}
              title={t('reminders.empty.title')}
              hint={t('reminders.empty.hint')}
            />
          ) : (
            <TableShell>
              <THead>
                <tr>
                  <Th>{t('reminders.colKind')}</Th>
                  <Th>{t('reminders.colFireAt')}</Th>
                  <Th>{t('reminders.colFiredAt')}</Th>
                </tr>
              </THead>
              <tbody>
                {reminders.map((r) => (
                  <Tr key={r.id}>
                    <Td className="font-medium text-slate-900 dark:text-white">{r.kind}</Td>
                    <Td>{formatDateTime(r.fireAt, i18n.language)}</Td>
                    <Td>{formatDateTime(r.firedAt, i18n.language)}</Td>
                  </Tr>
                ))}
              </tbody>
            </TableShell>
          )}
        </section>
      </div>
    </AppLayout>
  );
}
